/**
 * Task roulette (docs/roulette.md, docs/roadmap-2026-10.md flag `rouletteUI`). Owner, 2026-10-05:
 * "When there's a task that's been sitting a while with nobody doing it, task roulette starts. A
 * wheel of fortune style spinner comes up and assigns it to someone. This doesn't gate other people
 * from completing it, but if someone else does it, the person it's assigned to loses half the gold
 * it was worth."
 *
 * Pure rules, words and the wheel's geometry, shared by the server (server/roulette.ts, server/db.ts
 * completeTask), both clients' wheel overlay and task screen line, scripts/make-roulette.mjs (the
 * segment colours) and the Node tests (tests/roulette.test.ts).
 */
import { GOLD, openTaskGold, type BountyTask } from "./bounty.ts";
import { localDay } from "./flash.ts";
import { zonedInstant } from "./weekly.ts";

/** A task nobody has touched for three days is spun. */
export const ROULETTE_AFTER_MS = 3 * 24 * 3_600_000;

/** The wheel needs at least this many eligible peons to spin. */
export const ROULETTE_MIN_PEONS = 2;

/** The picked peon's gold for the task is doubled, after every other bonus (owner, 2026-10-05). */
export const ROULETTE_MULTIPLIER = GOLD.rouletteMultiplier;

/** household_settings key: who the wheel can pick (a JSON array of names; absent means everyone). */
export const ROULETTE_ELIGIBLE_KEY = "roulette_eligible";

/** How the wheel was settled: the picked peon did it, someone else did, or the task was deleted. */
export type RouletteOutcome = "winner" | "other" | "deleted";

/**
 * Who spun the wheel (migrations/0045_roulette_source.sql): the sweep (a real spin, one a day at
 * most) or the workshop's "Spin the wheel now" (a try, never counted toward the day).
 */
export type RouletteSource = "sweep" | "workshop";

/** One row of `roulettes` (migrations/0043_roulette.sql, `source` from 0045), the wheel parsed. */
export interface Roulette {
  id: string;
  task_id: string;
  wheel: string[];
  winner: string;
  stake: number;
  spun_at: string;
  settled_at: string | null;
  outcome: RouletteOutcome | null;
  done_by: string | null;
  /** Who spun it. The server always says; the clients show both kinds alike, so it is optional to them. */
  source?: RouletteSource;
}

/** GET /api/roulette: the household's spins, newest first, with the task's title and whether it is still open. */
export interface RouletteSpin extends Roulette {
  task_title: string | null;
  task_open: boolean;
}

export interface RouletteResponse {
  spins: RouletteSpin[];
}

/** Same name, ignoring case and spacing (names are matched case-insensitively everywhere). */
export function sameName(a: string | null | undefined, b: string | null | undefined): boolean {
  const k = (s: string) => s.trim().replace(/\s+/g, " ").toLowerCase();
  return typeof a === "string" && typeof b === "string" && k(a) === k(b);
}

// ---------- The trigger ----------

/** The task fields the trigger reads. */
export interface RouletteTaskFields {
  status: string;
  created_at: string;
  luxury?: number | null;
  schedule_id?: string | null;
  cadence?: string | null;
  occurrence_key?: string | null;
}

/**
 * Every rule but the age: open, not luxury, not from a schedule, nobody on it. The workshop's "Spin
 * the wheel now" (POST /api/roulette/spin) spins by this alone, so the two never diverge.
 */
export function rouletteCandidate(task: RouletteTaskFields, claimedBy: string | null): boolean {
  if (task.status !== "open") return false;
  if (task.luxury) return false;
  if (task.schedule_id || task.cadence || task.occurrence_key) return false;
  return !claimedBy;
}

/**
 * Whether an open task may be spun at `now`: open, not luxury, not from a schedule (owner, 2026-10-05:
 * "Scheduled tasks are exempt", "So are luxury"), posted at least ROULETTE_AFTER_MS ago, and nobody
 * holds a live claim on it (`claimedBy`, shared/claims.ts activeClaim). Whether it was spun before and
 * the flag and the peon count are the server's to check.
 */
export function rouletteQualifies(task: RouletteTaskFields, now: number, claimedBy: string | null): boolean {
  if (!rouletteCandidate(task, claimedBy)) return false;
  const at = Date.parse(task.created_at);
  return !Number.isNaN(at) && now - at >= ROULETTE_AFTER_MS;
}

// ---------- One a day ----------

/**
 * The household's local calendar day of an instant, "YYYY-MM-DD", in the household's zone (the one
 * the sweep and the weekly scroll use, server/weekly.ts weeklyZone). Same formula as the gold rush's
 * day (shared/flash.ts localDay).
 */
export function rouletteDay(at: Date | string, zone: string): string {
  return localDay(typeof at === "string" ? new Date(at) : at, zone);
}

/** The first instant of the household's day `day` ("YYYY-MM-DD") in `zone`: a spin at or after it is that day's or later. */
export function rouletteDayStart(day: string, zone: string): Date {
  const [y, m, d] = day.split("-").map(Number);
  return zonedInstant(y, m, d, 0, zone);
}

/**
 * Whether the sweep may spin today (owner, 2026-10-05: "There should be max one roulette a day, spins
 * from dev tools don't count"): `today` is the household's day key now, `sweepSpinDays` the day keys
 * of the sweep's own spins (never the workshop's). At most one a day, however many tasks qualify.
 */
export function sweepMaySpin(today: string, sweepSpinDays: readonly string[]): boolean {
  return !sweepSpinDays.includes(today);
}

/**
 * The names the wheel may pick: the household's names (`all`, as GET /api/peons lists them) kept to
 * the steward's list when there is one (`eligible`, compared ignoring case; a name no longer in the
 * household drops out). Null means everyone.
 */
export function eligibleNames(all: readonly string[], eligible: readonly string[] | null): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const n of all) {
    const key = n.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    if (eligible && !eligible.some((e) => sameName(e, n))) continue;
    seen.add(key);
    out.push(n);
  }
  return out;
}

/** The stored list, or null when absent or unreadable (everyone). */
export function parseEligible(raw: string | null | undefined): string[] | null {
  if (raw === null || raw === undefined) return null;
  try {
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && x.trim() !== "") : null;
  } catch {
    return null;
  }
}

/** Whether `name` is ticked on the House screen: everyone while there is no list. */
export function isEligible(name: string, eligible: readonly string[] | null): boolean {
  return eligible === null || eligible.some((e) => sameName(e, name));
}

/** The list after ticking or unticking `name`, starting from everyone (`all`) when there is no list yet. */
export function toggleEligible(all: readonly string[], eligible: readonly string[] | null, name: string, on: boolean): string[] {
  const now = eligible === null ? [...all] : eligible.filter((e) => all.some((n) => sameName(n, e)));
  const without = now.filter((e) => !sameName(e, name));
  return on ? [...without, name] : without;
}

// ---------- The spin ----------

/**
 * The spin: `names` shuffled into wheel order (Fisher-Yates with `rand`), then one of them picked
 * with the same `rand`. Pure: the same rand gives the same wheel and winner. The server stores both,
 * so every device draws the same wheel and lands on the same name.
 */
export function spinWheel(names: readonly string[], rand: () => number): { wheel: string[]; winner: string } {
  if (names.length === 0) throw new Error("spinWheel needs at least one name");
  const wheel = [...names];
  for (let i = wheel.length - 1; i > 0; i--) {
    const j = Math.min(i, Math.floor(rand() * (i + 1)));
    [wheel[i], wheel[j]] = [wheel[j], wheel[i]];
  }
  const winner = wheel[Math.min(wheel.length - 1, Math.floor(rand() * wheel.length))];
  return { wheel, winner };
}

/**
 * What the picked peon loses if someone else does it: half the task's plain gold at spin time
 * (shared/bounty.ts openTaskGold, no gold rush, no streak), rounded half up, never below 1.
 */
export function rouletteStake(task: BountyTask, at: number): number {
  return Math.max(1, Math.round(openTaskGold(task, at) / 2));
}

/** The charge when someone else does it: the stake, but never taking the purse below zero. */
export function rouletteCharge(stake: number, balance: number): number {
  return Math.max(0, Math.min(stake, Math.floor(balance)));
}

// ---------- The wheel ----------

/**
 * The most segments a wheel has, clockwise from the top: the wheel has exactly one per name (2 to
 * 12), each its own colour (owner, 2026-10-05: "The wheel should not be fixed segment number").
 * Beyond twelve names (never in practice) it keeps twelve and the thirteenth onward share.
 */
export const SEGMENTS = 12;
/** The fewest segments a sheet is drawn with (the wheel needs two peons). */
export const MIN_SEGMENTS = 2;

/** How many segments the wheel for these names has: one per name, 2 to 12. */
export function segmentCount(names: number): number {
  return Math.min(SEGMENTS, Math.max(MIN_SEGMENTS, Math.floor(names) || 0));
}

/** The sprite sheet for a wheel of `names` names (scripts/make-roulette.mjs): "roulette-wheel-3". */
export function wheelSheetFor(names: number): string {
  return `roulette-wheel-${segmentCount(names)}`;
}
/** The art: 96 by 96 pixels, drawn at two CSS pixels each. */
export const WHEEL_SIZE = 96;
export const WHEEL_SCALE = 2;
/** The pointer above the wheel: 14 by 10 art pixels. */
export const POINTER_W = 14;
export const POINTER_H = 10;
/** The sheet's frames: the wheel turned 0, 15, 30 ... 345 degrees clockwise. */
export const STEP_DEG = 15;
export const WHEEL_FRAMES = 360 / STEP_DEG;

/**
 * The segment colours, one per segment in wheel order, clockwise from the top, from the app's
 * palette (scripts/verify-ui.mjs): red, gold, green, parchment, purple, orange, light green,
 * lavender, then for nine to twelve names mid green, light orange, mid lavender, tan. Neighbours
 * differ in lightness or hue so each segment reads on its own.
 */
export const SEGMENT_COLOURS: readonly string[] = [
  "#e04a32",
  "#f0c14b",
  "#3f7d3a",
  "#f3e6c8",
  "#8a4fc8",
  "#d8731a",
  "#7bc47f",
  "#e0c8ff",
  "#5a9a4e",
  "#ffb75c",
  "#b98ae6",
  "#c8925a",
];

/**
 * The segment a name owns, by its place on the wheel: one each, so the wheel has as many segments
 * as names and each name one colour (owner, 2026-10-05: "I don't understand why some people have
 * multiple colours on the roulette"). With more than twelve names the wheel keeps twelve segments
 * and the thirteenth onward share the colours of the first twelve (name 13 shares segment 0 with
 * name 1).
 */
export function segmentsOf(wheel: readonly string[], index: number): number[] {
  if (wheel.length === 0 || index < 0 || index >= wheel.length) return [];
  return [index % SEGMENTS];
}

/** The legend under the wheel, in wheel order: each name with its segment's colour. */
export function wheelLegend(wheel: readonly string[]): { name: string; colours: string[] }[] {
  return wheel.map((name, i) => ({ name, colours: segmentsOf(wheel, i).map((s) => SEGMENT_COLOURS[s]) }));
}

/** The segment the pointer lands on: the winner's. */
export function landingSegment(wheel: readonly string[], winner: string): number {
  const i = wheel.findIndex((n) => sameName(n, winner));
  return segmentsOf(wheel, Math.max(0, i))[0] ?? 0;
}

/**
 * The sheet frame the wheel rests on so the pointer (at the top) sits inside the winner's segment.
 * Frame k is the wheel turned 15k degrees clockwise, so the pointer reads the wheel at -15k. With N
 * segments, segment s spans 360s/N to 360(s+1)/N; the wheel rests on the 15-degree step nearest the
 * segment's middle (a tie goes to the earlier step, so eight segments land 15 degrees in; twelve
 * segments of 30 land on the middle exactly). The step is at most 7.5 degrees off the middle and a
 * segment is at least 30 wide, so the pointer is always at least 7.5 degrees inside it, never on an
 * edge.
 */
export function landingFrame(wheel: readonly string[], winner: string): number {
  const span = 360 / segmentCount(wheel.length);
  const middle = (landingSegment(wheel, winner) + 0.5) * span;
  const step = Math.ceil(middle / STEP_DEG - 0.5 - 1e-9);
  return (((WHEEL_FRAMES - step) % WHEEL_FRAMES) + WHEEL_FRAMES) % WHEEL_FRAMES;
}

/** About three seconds of hard steps. */
export const SPIN_MS = 3000;
/** Whole turns before the landing. */
const SPIN_TURNS = 3;

/**
 * The spin as hard steps (docs/product.md "Motion": no easing, 15 degrees a step): the frame to show
 * at each step and how long it holds, ending on `landingFrame`. It speeds up briefly, then slows,
 * about SPIN_MS in all. Reduced motion skips it: one step, already landed.
 */
export function spinSteps(wheel: readonly string[], winner: string, reduced = false): { frame: number; ms: number }[] {
  const end = landingFrame(wheel, winner);
  if (reduced) return [{ frame: end, ms: 0 }];
  const count = SPIN_TURNS * WHEEL_FRAMES + end;
  const raw: number[] = [];
  for (let k = 1; k <= count; k++) {
    const t = k / count;
    // The first eighth speeds up (long holds to short), the rest slows (short to long).
    raw.push(t < 0.125 ? 1 - (t / 0.125) * 0.8 : 0.2 + Math.pow((t - 0.125) / 0.875, 2.2) * 3);
  }
  const sum = raw.reduce((a, b) => a + b, 0);
  return raw.map((r, i) => ({ frame: (i + 1) % WHEEL_FRAMES, ms: Math.max(16, Math.round((r / sum) * SPIN_MS)) }));
}

// ---------- Words ----------

export const ROULETTE_TITLE = "Task roulette";
export const ROULETTE_DONE = "Got it";
/** The House screen: the section's one line. */
export const ROULETTE_ELIGIBLE_LINE = "Who the wheel can pick";

const isMe = (winner: string, me: string | null | undefined) => sameName(winner, me);

/** "The wheel picked Hue" / "The wheel picked you". */
export function pickedLine(winner: string, me: string | null | undefined): string {
  return `The wheel picked ${isMe(winner, me) ? "you" : winner}`;
}

/** The overlay's line under it: "Double gold for Hue; if someone else does it, Hue loses 25 gold." */
export function stakeLine(winner: string, me: string | null | undefined, stake: number): string {
  return isMe(winner, me)
    ? `Double gold if you do it; if someone else does it, you lose ${stake} gold.`
    : `Double gold for ${winner}; if someone else does it, ${winner} loses ${stake} gold.`;
}

/**
 * The task screen's muted line under the meta: "Assigned to Hue" / "Assigned to you". Just that
 * (owner, 2026-10-05, of the old line spelling out the double gold and the stake: "This explaination
 * text is terrible, remove it. Just have text that says assigned to"); the wheel's overlay carries
 * the stake. The stake is still passed so the line shows only while a wheel is unsettled.
 */
export function taskRouletteLine(winner: string, me: string | null | undefined, _stake: number): string {
  return isMe(winner, me) ? "Assigned to you" : `Assigned to ${winner}`;
}

/**
 * The push to everyone at the spin, the picked peon included: the task, never who it picked, so the
 * wheel is not given away before it spins on anyone's screen (owner, 2026-10-06: "notify that a
 * roulette happens to everyone right away, but don't notify who it picked").
 */
export function spinPush(title: string): { title: string; body: string } {
  return { title: ROULETTE_TITLE, body: `The wheel is spinning for ${title}. Open up and see.` };
}

/** The push to the picked peon when someone else did it and they were charged. */
export function chargePush(doneBy: string, title: string, charged: number): { title: string; body: string } {
  return { title: ROULETTE_TITLE, body: `${doneBy} did ${title} first. ${charged} gold to the house.` };
}

/** The overlay's accessible name. */
export function overlayLabel(taskTitle: string | null): string {
  return taskTitle ? `${ROULETTE_TITLE}: ${taskTitle}` : ROULETTE_TITLE;
}

// ---------- Which spins a device shows ----------

/** This device's shown spins (`op.rouletteSeen`): a JSON array of roulette ids. */
export const ROULETTE_SEEN_KEY = "op.rouletteSeen";
/** How many ids the device keeps (the newest). */
export const ROULETTE_SEEN_MAX = 200;

export function parseSeen(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const v: unknown = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function addSeen(seen: readonly string[], ids: readonly string[]): string[] {
  const out = [...seen.filter((s) => !ids.includes(s)), ...ids];
  return out.slice(Math.max(0, out.length - ROULETTE_SEEN_MAX));
}

/**
 * The spins this device should show now, in the order they show: unseen, the task still open and
 * the wheel unsettled, oldest first so the newest shows last. A spin whose task is done or gone is
 * not shown (`stale`, for the caller to mark seen quietly).
 */
export function spinsToShow(spins: readonly RouletteSpin[], seen: readonly string[]): { show: RouletteSpin[]; stale: string[] } {
  const unseen = spins.filter((s) => !seen.includes(s.id));
  const live = (s: RouletteSpin) => s.task_open && s.settled_at === null;
  const show = unseen.filter(live).sort((a, b) => (a.spun_at < b.spun_at ? -1 : a.spun_at > b.spun_at ? 1 : 0));
  return { show, stale: unseen.filter((s) => !live(s)).map((s) => s.id) };
}

// ---------- The wheel before the task ----------
//
// Owner, 2026-10-09: "When pressing a roulette notification, it navigates to the task, revealing the
// assignment before actually observing the roulette". A task whose spin this device has not watched
// never shows its screen first: the way onto it (a push, a shared link, a board card) shows the wheel,
// and Got it then opens the task (src/roulette.ts, client/lib/roulette.ts `wheelBeforeTask`).

/**
 * The spin this device should watch before task `taskId`'s screen opens, or null: the one
 * `spinsToShow` would show for that task (unseen here, the task open, the wheel unsettled). A task
 * is spun once, ever, so there is at most one.
 */
export function spinForTask(spins: readonly RouletteSpin[], seen: readonly string[], taskId: string): RouletteSpin | null {
  return spinsToShow(spins, seen).show.find((s) => s.task_id === taskId) ?? null;
}

/** How long a task asked for waits for the spins read before it opens anyway. */
export const TASK_WAIT_MS = 5_000;

/** The tag on the spin's push and the charge push (server/roulette.ts spinMessage, chargeMessage): "roulette-<taskId>". */
export const ROULETTE_TAG_PREFIX = "roulette-";

/** Whether a push is a roulette push (by its tag): an app already running reads the spins again. */
export function isRouletteTag(tag: unknown): boolean {
  return typeof tag === "string" && tag.startsWith(ROULETTE_TAG_PREFIX);
}

// ---------- The workshop ----------

/** POST /api/roulette/spin's 409 when no task can be spun (the workshop's "Spin the wheel now"). */
export const SPIN_NOTHING = "Nothing to spin. Add a task by hand that nobody has started.";
/** Its 409 when fewer than two peons are left ticked on the House screen. */
export const SPIN_TOO_FEW = "Nothing to spin. The wheel needs two peons it can pick.";
